import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportCashFlow } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function CashFlowReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-cash-flow', qBuilt],
    queryFn: () => fetchReportCashFlow(qBuilt),
  });

  const d = q.data;

  const chartRows = useMemo(
    () =>
      (d?.chart ?? []).map((r) => ({
        ...r,
        dLabel: r.date.length > 10 ? r.date.slice(0, 10) : r.date,
        inflowNum: Number(r.inflow),
        outflowNum: Number(r.outflow),
        netNum: Number(r.net),
      })),
    [d],
  );

  const csv = () => {
    if (!d) return;
    exportToCsv(
      d.chart.map((r) => ({ date: r.date, inflow: r.inflow, outflow: r.outflow, net: r.net })),
      'cash-flow.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading cash flow…</div>;
  if (q.isError || !d?.summary || !d.byMethod || !d.cashSessions || !d.chart) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load cash flow report.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'Net cash flow', v: formatCurrency(d.summary.netCashFlow) },
          { label: 'Total inflow', v: formatCurrency(d.summary.totalInflow) },
          { label: 'Total outflow', v: formatCurrency(d.summary.totalOutflow) },
          { label: 'Period variance total', v: formatCurrency(d.summary.varianceTotal ?? 0) },
          {
            label: 'Sessions over tolerance',
            v: String(d.summary.overToleranceCount ?? 0),
          },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Cash flow over time</h3>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="dLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="inflowNum" name="Inflow" stroke="#22c55e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="outflowNum" name="Outflow" stroke="#ef4444" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="netNum" name="Net" stroke="#6366f1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">By payment method</h3>
          <table className="mt-4 w-full text-left text-sm">
            <thead className="text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="py-2">Method</th>
                <th className="py-2">Inflow</th>
                <th className="py-2">Outflow</th>
              </tr>
            </thead>
            <tbody>
              {d.byMethod.map((m) => (
                <tr key={m.method} className="border-t border-slate-100">
                  <td className="py-2 font-medium capitalize">{prettyLabel(m.method)}</td>
                  <td className="py-2 text-emerald-700">{formatCurrency(m.inflow)}</td>
                  <td className="py-2 text-red-700">{formatCurrency(m.outflow)}</td>
                </tr>
              ))}
              {d.byMethod.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-slate-500">No payment activity.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {(d.varianceByEmployee?.length ?? 0) > 0 ? (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-bold text-slate-950">Variance by employee</h3>
            <p className="text-xs text-slate-500">
              Grouped by who closed the drawer (falls back to who opened). Tolerance:{' '}
              {formatCurrency(d.summary.cashVarianceTolerance ?? 5)}.
            </p>
          </div>
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
              <tr>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Sessions</th>
                <th className="px-4 py-3">Variance total</th>
                <th className="px-4 py-3">Over tolerance</th>
              </tr>
            </thead>
            <tbody>
              {d.varianceByEmployee!.map((e) => (
                <tr key={e.userId} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-medium">{e.name}</td>
                  <td className="px-4 py-2">{e.sessionCount}</td>
                  <td
                    className={`px-4 py-2 font-semibold ${
                      Number(e.varianceTotal) < 0 ? 'text-red-600' : 'text-emerald-600'
                    }`}
                  >
                    {formatCurrency(e.varianceTotal)}
                  </td>
                  <td className="px-4 py-2">
                    {e.overToleranceCount > 0 ? (
                      <span className="font-semibold text-red-600">{e.overToleranceCount}</span>
                    ) : (
                      e.overToleranceCount
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Opened</th>
              <th className="px-4 py-3">Closed</th>
              <th className="px-4 py-3">Closed by</th>
              <th className="px-4 py-3">Expected cash</th>
              <th className="px-4 py-3">Actual cash</th>
              <th className="px-4 py-3">Variance</th>
            </tr>
          </thead>
          <tbody>
            {d.cashSessions.map((s) => (
              <tr
                key={s.id}
                className={`border-b border-slate-100 ${s.overTolerance ? 'bg-red-50/50' : ''}`}
              >
                <td className="px-4 py-2">{formatDateTime(s.openedAt)}</td>
                <td className="px-4 py-2">{s.closedAt ? formatDateTime(s.closedAt) : 'Open'}</td>
                <td className="px-4 py-2">{s.closedByName ?? s.openedByName ?? '—'}</td>
                <td className="px-4 py-2">{formatCurrency(s.expectedCash)}</td>
                <td className="px-4 py-2">{s.actualCash != null ? formatCurrency(s.actualCash) : '—'}</td>
                <td
                  className={`px-4 py-2 font-semibold ${
                    Number(s.variance ?? 0) < 0 ? 'text-red-600' : 'text-emerald-600'
                  }`}
                >
                  {s.variance != null ? formatCurrency(s.variance) : '—'}
                  {s.overTolerance ? ' ⚠' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.cashSessions.length ? <p className="p-6 text-center text-slate-500">No cash sessions in this period.</p> : null}
      </div>
    </div>
  );
}
